import { useState } from "react"
import { IconBoltOutline18 as Bolt } from "nucleo-ui-outline-18"

import type { SidebarSectionActionButtonComponent } from "@productlane/api/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { AlertDialog, Button } from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useSidebarSectionContext } from "./section-context"

interface Props {
	component: SidebarSectionActionButtonComponent
	fullWidth?: boolean
}

export function ActionButtonComponent({ component, fullWidth = false }: Props) {
	const { sectionId, target, onActionResult } = useSidebarSectionContext()
	const [isConfirmOpen, setIsConfirmOpen] = useState(false)
	const execute = api.customData.executeAction.useMutation()

	const run = async () => {
		try {
			const result = await execute.mutateAsync({
				cardId: sectionId,
				target,
				action: component.action,
				input: component.input ?? {},
			})
			if (result.success) {
				toast.success({
					title: result.message ?? `${component.label} done`,
					icon: Bolt,
				})
				onActionResult(result.components)
			} else {
				toast.info({
					title: result.message ?? `${component.label} did not run`,
					icon: Bolt,
				})
			}
		} catch (error) {
			toast.error({
				title: `Couldn't run ${component.label}`,
				description: error instanceof Error ? error.message : undefined,
				icon: Bolt,
			})
		}
	}

	return (
		<>
			<Button
				variant={component.variant === "negative" ? "danger" : "outline"}
				size="sm"
				className={cn("max-w-full px-3", fullWidth && "w-full")}
				isLoading={execute.isLoading}
				onClick={() => {
					if (component.confirm) {
						setIsConfirmOpen(true)
						return
					}
					void run()
				}}
			>
				<span className="truncate">{component.label}</span>
			</Button>

			{component.confirm ? (
				<AlertDialog
					isOpen={isConfirmOpen}
					onOpenChange={setIsConfirmOpen}
					title={component.confirm.title}
					description={component.confirm.description ?? ""}
					confirmText={component.confirm.confirmText ?? component.label}
					variant={component.variant === "negative" ? "negative" : "positive"}
					onCancel={() => setIsConfirmOpen(false)}
					onConfirm={run}
				/>
			) : null}
		</>
	)
}
